import { AxiosError } from 'axios';
import {
  ChartCategoryTypes,
  Transcript,
  SubjectArea,
  DemographicChartLabels,
  SelectedTranscriptsIdsByCategory,
  TranscripsCategoriesKeys,
  CategoriesWithValues,
  CategorizedSelectedTranscriptsIds,
  SelectedTranscriptsSummary,
} from '../../interfaces/TranscriptsInterface';
import { UserDetails } from '../../interfaces/UserDetailsInterface';
import { saveSingleSelection, unsaveSingleSelection } from '../../services/transcriptsService';
import { DemographicsSummary } from '../../interfaces/DataQcInterface';
import { DemographicCategoryOption } from '../../interfaces/EOAInterface';
import { TRANSCRIPTS_CATEGORIES } from '../../constants';

const chartCategories = [
  ChartCategoryTypes.RACE,
  ChartCategoryTypes.GENDER,
  ChartCategoryTypes.STUDENT_WITH_DISABILITIES,
  ChartCategoryTypes.ENGLISH_LEARNER,
  ChartCategoryTypes.LOW_INCOME,
];

type categoriesWithValues = { [key in ChartCategoryTypes]: { [key: string]: number } };

export const getCategoryCountsByType = (data: Transcript[]) => {
  const categoryCountsByType: categoriesWithValues = {
    [ChartCategoryTypes.RACE]: {},
    [ChartCategoryTypes.GENDER]: {},
    [ChartCategoryTypes.STUDENT_WITH_DISABILITIES]: {},
    [ChartCategoryTypes.ENGLISH_LEARNER]: {},
    [ChartCategoryTypes.LOW_INCOME]: {},
  };

  data.forEach((dataItem) => {
    chartCategories.forEach((chartCategory) => {
      let currentCategoryData = categoryCountsByType[chartCategory];
      let subCategoryValue = dataItem[chartCategory];

      if (currentCategoryData.hasOwnProperty(subCategoryValue)) {
        currentCategoryData[subCategoryValue]++;
      } else {
        currentCategoryData[subCategoryValue] = 1;
      }
    });
  });

  return categoryCountsByType;
};

export const calcSelectedTranscriptsDemographicPercentages = (
  demographicsSummary: DemographicsSummary | undefined,
  selectedTranscripts: Transcript[]
) => {
  // display the percentage of the current selection
  const selectedTranscriptsDemographicPercentages: categoriesWithValues = {
    [ChartCategoryTypes.RACE]: {},
    [ChartCategoryTypes.GENDER]: {},
    [ChartCategoryTypes.STUDENT_WITH_DISABILITIES]: {},
    [ChartCategoryTypes.ENGLISH_LEARNER]: {},
    [ChartCategoryTypes.LOW_INCOME]: {},
  };

  if (!demographicsSummary) return selectedTranscriptsDemographicPercentages;

  const selectedCount = getCategoryCountsByType(selectedTranscripts);

  let totalDemographicsPercentages = demographicsSummary.total.demographic_percentages;
  for (const categoryType in totalDemographicsPercentages) {
    const subCategoriesObject = totalDemographicsPercentages[categoryType as ChartCategoryTypes];
    const selectedSubCategoriesObject = selectedCount[categoryType as ChartCategoryTypes] || {};

    for (const key in subCategoriesObject) {
      if (selectedTranscripts.length === 0)
        selectedTranscriptsDemographicPercentages[categoryType as ChartCategoryTypes][key] = 0;
      else {
        const selectedSubCategoryCount = selectedSubCategoriesObject[key] || 0;

        let formattedPercentage = Number(((selectedSubCategoryCount / selectedTranscripts.length) * 100).toFixed(1));
        selectedTranscriptsDemographicPercentages[categoryType as ChartCategoryTypes][key] = formattedPercentage;
      }
    }
  }

  return selectedTranscriptsDemographicPercentages;
};

type DemographicCategoriesData = DemographicsSummary['total']['demographic_percentages'];

export const extractChartData = (categoryData: { [key: string]: number }) => {
  const labels: string[] = [];
  const data: number[] = [];

  Object.entries(categoryData).forEach(([key, value]) => {
    const label = DemographicChartLabels[key as keyof typeof DemographicChartLabels] || key;
    labels.push(label);
    data.push(value);
  });

  return { labels, data };
};

export const buildChartData = (
  data: DemographicCategoriesData | undefined,
  chartCategories: ChartCategoryTypes[],
  barColor: string
) => {
  let combinedLabels: string[] = [];
  let combinedData: number[] = [];

  if (!data) {
    return { labels: [], dataSet: { data: [], backgroundColor: barColor } };
  }

  // console.log(data, chartCategories);
  chartCategories.forEach((chartCategory) => {
    let { labels, data: extractedData } = extractChartData(data[chartCategory]);
    combinedLabels = combinedLabels.concat(labels);
    combinedData = combinedData.concat(extractedData);
  });

  return { labels: combinedLabels, dataSet: { data: combinedData, backgroundColor: barColor } };
};

export const createChartConfigs = (
  data: DemographicCategoriesData,
  demographicCategories: DemographicCategoryOption[],
  barColor: string
) => {
  const categoryDisplayNames: { [key: string]: string } = {};
  demographicCategories.forEach((category) => {
    categoryDisplayNames[category.name] = category.display_name;
  });

  let raceChartData = buildChartData(data, [ChartCategoryTypes.RACE], barColor);
  let genderChartData = buildChartData(data, [ChartCategoryTypes.GENDER], barColor);
  let swdChartData = buildChartData(data, [ChartCategoryTypes.STUDENT_WITH_DISABILITIES], barColor);
  let ellChartData = buildChartData(data, [ChartCategoryTypes.ENGLISH_LEARNER], barColor);
  let liChartData = buildChartData(data, [ChartCategoryTypes.LOW_INCOME], barColor);

  let raceChartConfig = {
    title: categoryDisplayNames['RACE'] || 'Race/Ethnicity',
    labels: raceChartData.labels,
    dataSet: raceChartData.dataSet,
  };

  let genderChartConfig = {
    title: categoryDisplayNames['GENDER'] || 'Gender',
    labels: genderChartData.labels,
    dataSet: genderChartData.dataSet,
  };

  let swdChartConfig = {
    title: categoryDisplayNames['SWD'] || 'Disability status',
    labels: swdChartData.labels,
    dataSet: swdChartData.dataSet,
  };

  let ellChartConfig = {
    title: categoryDisplayNames['ELL'] || 'English language learner status',
    labels: ellChartData.labels,
    dataSet: ellChartData.dataSet,
  };

  let liChartConfig = {
    title: categoryDisplayNames['LI'] || 'Socioeconomic status',
    labels: liChartData.labels,
    dataSet: liChartData.dataSet,
  };

  const configuredCategoryNames = new Set(demographicCategories.map((category) => category.name));

  return [raceChartConfig, genderChartConfig, swdChartConfig, ellChartConfig, liChartConfig].filter((config, index) => {
    const categoryNames = ['RACE', 'GENDER', 'SWD', 'ELL', 'LI'];
    return configuredCategoryNames.has(categoryNames[index]);
  });
};

export const formatHelperTextCount = (count: number, itemType: string) => {
  return `${count} ${count === 1 ? itemType : `${itemType}s`}`;
};

const sortTableHeaderSubjectAreas = (tableHeaderData: { key: string; label: string; subLabel?: string }[]) => {
  const groupedPairs = [];

  for (let i = 0; i < tableHeaderData.length; i++) {
    const current = tableHeaderData[i];
    const next = tableHeaderData[i + 1];

    if (current.subLabel && next && !next.subLabel) {
      groupedPairs.push([current, next]);
      i++;
    }
  }

  groupedPairs.sort((a, b) => a[0].label.localeCompare(b[0].label));

  return groupedPairs.flat();
};

export function prepareTranscriptDetailsData(terms: any[]) {
  const tableHeaderData: { key: string; label: string; subLabel?: string }[] = [];

  const subjects = new Map<string, number>();
  let creditsPerGrade = 0;

  terms.forEach((term) => {
    term.subject_areas.forEach((subjectArea: SubjectArea) => {
      const credits = subjectArea.credits_earned ?? 0;
      creditsPerGrade += credits;
      subjects.set(subjectArea.name, (subjects.get(subjectArea.name) || 0) + credits);
    });
  });

  subjects.forEach((creditCount, subjectName) => {
    tableHeaderData.push({
      key: subjectName,
      label: subjectName,
      subLabel: formatHelperTextCount(creditCount, 'credit'),
    });
    tableHeaderData.push({ key: `${subjectName}-grade`, label: '' });
  });

  const sortedTableHeaderData = sortTableHeaderSubjectAreas(tableHeaderData);

  return { tableHeaderData: sortedTableHeaderData, creditsPerGrade };
}

const addToSelectedTranscripts = (
  callback: (value: React.SetStateAction<SelectedTranscriptsSummary>) => void,
  category: string,
  transcript: Transcript
) => {
  callback((prev) => ({
    ...prev,
    selection_counts: {
      ...prev.selection_counts,
      [category]: (prev.selection_counts[category] || 0) + 1,
    },
    selectedTranscriptsByCategory: {
      ...prev.selectedTranscriptsByCategory,
      [category]: [...prev.selectedTranscriptsByCategory[category], transcript],
    },
  }));
};

const removeFromSelectedTranscripts = (
  callback: (value: React.SetStateAction<SelectedTranscriptsSummary>) => void,
  category: string,
  transcriptId: string
) => {
  callback((prev) => ({
    ...prev,
    selection_counts: {
      ...prev.selection_counts,
      [category]: Math.max((prev.selection_counts[category] || 0) - 1, 0),
    },
    selectedTranscriptsByCategory: {
      ...prev.selectedTranscriptsByCategory,
      [category]:
        prev.selectedTranscriptsByCategory[category].filter(
          (selectedTranscript) => selectedTranscript.id !== transcriptId
        ) || [],
    },
  }));
};

type OnErrorCallBack = (errorMessage: string, error: Error | AxiosError) => void;

export const selectTranscript = async (
  userDetails: UserDetails | undefined,
  category: TranscripsCategoriesKeys,
  transcript: Transcript,
  isSelected: boolean,
  schoolId: string,
  onError: OnErrorCallBack,
  onUpdateSelectedTranscriptsSummary: (value: React.SetStateAction<SelectedTranscriptsSummary>) => void,
  workflowStatus?: string | null,
  onRefreshWorkflowStatus?: () => Promise<void>
) => {
  if (!userDetails) {
    let error = new Error('Missing user details!');
    onError('Missing user details!', error as AxiosError);

    return;
  }

  const payload = { category: category, transcript_id: transcript.id };
  const shouldRefreshStatus = workflowStatus === 'TRANSCRIPT_SELECTION_NOT_STARTED';

  if (isSelected) {
    try {
      removeFromSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript.id);
      await unsaveSingleSelection(userDetails.eoaId, userDetails.access_token, schoolId, payload);
      if (shouldRefreshStatus && onRefreshWorkflowStatus) {
        await onRefreshWorkflowStatus();
      }
    } catch (error) {
      console.log(error);
      onError('Error occured while unsaving a selected transcript!', error as AxiosError);
      addToSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript);
    }
    return;
  }

  try {
    addToSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript);
    await saveSingleSelection(userDetails.eoaId, userDetails.access_token, schoolId, payload);
    if (shouldRefreshStatus && onRefreshWorkflowStatus) {
      await onRefreshWorkflowStatus();
    }
  } catch (error) {
    console.log(error);
    onError('Error occured while saving a selected transcript!', error as AxiosError);
    removeFromSelectedTranscripts(onUpdateSelectedTranscriptsSummary, category, transcript.id);
  }
};

export const prepareCategorizedData = (selectedTranscriptsByCategory: { [key: string]: Transcript[] }) => {
  const selectedIdsByCategory: SelectedTranscriptsIdsByCategory = {
    [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
    [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
    [TRANSCRIPTS_CATEGORIES.STRUGGLING]: [],
  };

  for (const category of Object.keys(TRANSCRIPTS_CATEGORIES)) {
    selectedIdsByCategory[category as keyof typeof TRANSCRIPTS_CATEGORIES] =
      selectedTranscriptsByCategory[category].map((transcript) => transcript.id) || [];
  }

  const categorizedData: CategorizedSelectedTranscriptsIds = {
    rigorous_academic_transcript_ids: [],
    on_the_cusp_transcript_ids: [],
    struggling_transcript_ids: [],
  };

  (Object.keys(selectedIdsByCategory) as TranscripsCategoriesKeys[]).forEach((category) => {
    const key = `${category.toLowerCase()}_transcript_ids`;
    categorizedData[key as keyof typeof categorizedData] = selectedIdsByCategory[category];
  });

  return categorizedData;
};

export const storeLastNavigatedPage = (
  lastNavigated: CategoriesWithValues<number> | null,
  categoryName: string,
  page: number
) => {
  if (!lastNavigated) {
    lastNavigated = {
      [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 1,
      [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 1,
      [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 1,
    };
  }

  return {
    ...lastNavigated,
    [categoryName]: page,
  };
};

//delete if not needed after 697 is finished
// export const extractLastPage = (category: string, linkHeader: string) => {
//   const match = linkHeader.match(new RegExp(`page=(\\d+)>; rel="last".*?${category}`, 'i'));
//   return match ? Number(match[1] + 1) : 0;
// };

export const checkForLink = (linkHeader: string, linkRel: string) => {
  const regex = new RegExp(`rel="${linkRel}"`, 'i');
  return regex.test(linkHeader);
};

export const checkForNextPage = (linkHeader: string): boolean => {
  const regex = /<[^>]+\?page=\d+>; rel="next"/i;
  return regex.test(linkHeader);
};

const formatHyphenated = (input: string) => {
  return input
    .split('-')
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
    .join('-');
};

const formatUnderscored = (input: string) => {
  return input
    .split('_')
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
    .join(' ');
};

const formatSlashed = (input: string) => {
  return input
    .split('/')
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
    .join('/');
};

export const formatToTitleCase = (input: string) => {
  const minorWords = ['or', 'for', 'of', 'and', 'but', 'in', 'on', 'at', 'by', 'to', 'with', 'the', 'a', 'an'];

  if (input.includes('-')) return formatHyphenated(input);

  return formatUnderscored(input)
    .split(' ')
    .map((word, index) => {
      if (word.includes('/')) return formatSlashed(word);

      const lowerCaseWord = word.toLowerCase();
      if (minorWords.includes(lowerCaseWord) && index !== 0) return lowerCaseWord;

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};
